import {
  EErrorMessageMode,
  IAndroidConfig,
  IApiConfig,
  IElectronConfig,
  IIosConfig,
  IWebConfig
} from "@kuika/kuika-cl-model";

/**
 * Defaults applied to `activeConfiguration` before it becomes the form's `initialValues` —
 * on load, and therefore before both `takeChangeSnapshot` and the object-hash baseline read
 * the form back.
 *
 * These values used to live in container `state`, loaded once off `activeConfiguration` with
 * their own defaults and written back at submit regardless of what the form held. Now that
 * every one of them is a real `Form.Item`, `getFieldsValue()` — and therefore the snapshot,
 * the hash, and `onFinish` — only ever sees what is MOUNTED, at whatever value it was last
 * given. A value this module does not normalize opens the configuration already "changed":
 * `isSameValue` treats `undefined`/`null`/`""` as one emptiness, but a switch the reader
 * toggles on and back off writes a real `false`, and `false !== undefined`. Colour is the
 * same problem in a different shape — a configuration saved as `#E1E5EB` and one saved as
 * `E1E5EB` must both open unchanged, so the `#` has to come off here, once, rather than in
 * every place that reads the colour.
 */

/** The colour a splash screen falls back to when the configuration has never set one. */
export const DEFAULT_SPLASH_BG_COLOR: string = "E1E5EB";

/**
 * The two switches that used to be container state: `2F Auth` and `Expire Old Sessions Upon
 * New Login`. Shared by the platform-pane editor and Run Settings — both host `ConfigTwoFa`
 * / `ConfigExpireOldSessions`, and both used to carry their own copy of this default.
 */
export const normalizeApiConfigSwitches = (apiConfig: IApiConfig): void => {
  if (apiConfig.isTwoFactorAuthenticationEnabled === undefined || apiConfig.isTwoFactorAuthenticationEnabled === null) {
    apiConfig.isTwoFactorAuthenticationEnabled = false;
  }
  if (apiConfig.isExpireOldSessionsEnabled === undefined || apiConfig.isExpireOldSessionsEnabled === null) {
    apiConfig.isExpireOldSessionsEnabled = false;
  }
};

/**
 * The Error Message Mode dropdown. Shared by the platform-pane editor and Run Settings. The
 * control shows Detailed for an absent value, so a configuration saved before the setting
 * existed has to open with Detailed in the form too — otherwise picking it writes a real `0`
 * against a snapshot holding `undefined`, and the rail marks a change that is not one.
 */
export const normalizeApiConfigErrorMessageMode = (apiConfig: IApiConfig): void => {
  if (apiConfig.errorMessageMode === undefined || apiConfig.errorMessageMode === null) {
    apiConfig.errorMessageMode = EErrorMessageMode.Detailed;
  }
};

/** The web favicon. Shared by the platform-pane editor and Run Settings. */
export const normalizeWebIcon = (webConfig: IWebConfig): void => {
  if (webConfig.appIcon === undefined) webConfig.appIcon = null;
};

/**
 * The Maps section's "let end users choose the map provider" switch, on web and Android. Shared
 * by the platform-pane editor and Run Settings (web only there).
 */
export const normalizeMapProviderChoiceSwitch = (config: IWebConfig | IAndroidConfig): void => {
  if (config.allowEndUserMapProviderChoice === undefined || config.allowEndUserMapProviderChoice === null) {
    config.allowEndUserMapProviderChoice = false;
  }
};

/** Android's two launcher icons and its splash screen (image, colour, fitting). */
export const normalizeAndroidIconAndSplash = (androidConfig: IAndroidConfig): void => {
  if (androidConfig.iconFilePath === undefined) androidConfig.iconFilePath = null;
  if (androidConfig.iconRoundedFileName === undefined) androidConfig.iconRoundedFileName = null;
  if (androidConfig.splashScreenImageName === undefined) androidConfig.splashScreenImageName = null;
  androidConfig.splashScreenBackcolor =
    androidConfig.splashScreenBackcolor?.replace(/#/i, "") || DEFAULT_SPLASH_BG_COLOR;
  androidConfig.imageFit = androidConfig.imageFit || "default";
};

/** iOS's app icon and its splash screen (image, colour, fitting). */
export const normalizeIosIconAndSplash = (iosConfig: IIosConfig): void => {
  if (iosConfig.iconFileName === undefined) iosConfig.iconFileName = null;
  if (iosConfig.splashScreenImageName === undefined) iosConfig.splashScreenImageName = null;
  iosConfig.splashScreenBackcolor = iosConfig.splashScreenBackcolor?.replace(/#/i, "") || DEFAULT_SPLASH_BG_COLOR;
  iosConfig.imageFit = iosConfig.imageFit || "default";
};

/**
 * Electron's app icon and its three checkbox groups (target platforms, architectures, Linux
 * package formats). The groups need a default too: an empty selection round-trips through a
 * `KuikaCheckboxGroup` as `[]`, and the snapshot holding `undefined` for the same field would
 * read that as a change — `isSameValue` compares arrays by contents, and `[]` is not `undefined`.
 */
export const normalizeElectronIconAndTargets = (electronConfig: IElectronConfig): void => {
  if (electronConfig.appIconFileName === undefined) electronConfig.appIconFileName = null;
  if (!electronConfig.targetPlatforms) electronConfig.targetPlatforms = [];
  if (!electronConfig.targetArchitectures) electronConfig.targetArchitectures = [];
  if (!electronConfig.linuxPackageFormats) electronConfig.linuxPackageFormats = [];
};
